import type { Widget } from "@/types";
import type { RegisterBannerWidget } from "@/types";
import type { WidgetResourcePath } from "@/components/widgets/constants";
import { getData } from "../base.ehq.model";
import { deserialize } from "../base.serializer";

const toArray = <T>(data: T | Array<T> | null | undefined): Array<T> => {
  if (!data) return [];
  return Array.isArray(data) ? data : [data];
};

export const getWidget = async (
  token: string,
  baseUrl: string,
  projectId: string,
  widgetId: string,
  config?: object
): Promise<Widget> => {
  const queryPath = `${baseUrl}/projects/${projectId}/widgets/${widgetId}`;
  const res = await getData(token, queryPath, {}, config);

  return (await deserialize(res.data)) as Widget;
};

export const getWidgetResources = async <T = unknown>(
  token: string,
  baseUrl: string,
  projectId: string,
  widgetId: string,
  resource: Exclude<WidgetResourcePath, "show" | "life_cycle_stages">,
  params?: object,
  config?: object
): Promise<Array<T>> => {
  const queryPath = `${baseUrl}/projects/${projectId}/widgets/${widgetId}/${resource}`;
  const queryParams =
    resource === "related_projects"
      ? { ...params, for_participant: true }
      : params;
  const res = await getData(token, queryPath, queryParams, config);

  return toArray<T>((await deserialize(res.data)) as T | Array<T>);
};

export const getLifeCycleStages = async <T = unknown>(
  token: string,
  baseUrl: string,
  projectId: string,
  params?: object,
  config?: object
): Promise<Array<T>> => {
  const queryPath = `${baseUrl}/projects/${projectId}/life_cycle_widget/stages`;
  const res = await getData(token, queryPath, params, config);

  return toArray<T>((await deserialize(res.data)) as T | Array<T>);
};

export const getWidgetFolders = async <T = unknown>(
  token: string,
  baseUrl: string,
  projectId: string,
  widgetId: string,
  params?: object,
  config?: object
): Promise<Array<T>> => {
  const queryPath = `${baseUrl}/projects/${projectId}/widgets/${widgetId}/folders`;
  const res = await getData(token, queryPath, params, config);

  return toArray<T>((await deserialize(res.data)) as T | Array<T>);
};

export const getRegisterBannerWidget = async (
  token: string,
  baseUrl: string,
  projectId: string,
  config?: object
): Promise<RegisterBannerWidget | null> => {
  const queryPath = `${baseUrl}/projects/${projectId}/register_banner_widget`;

  try {
    const res = await getData(token, queryPath, {}, config);
    return (await deserialize(res.data)) as RegisterBannerWidget;
  } catch (err: unknown) {
    if ((err as { status?: number })?.status === 404) return null;
    throw err;
  }
};

export interface ProjectBlogPost {
  id: string;
  state?: string;
  tagList?: Array<string> | string;
}

export const getProjectBlogPosts = async (
  token: string,
  baseUrl: string,
  projectId: string,
  config?: object
): Promise<Array<ProjectBlogPost>> => {
  const queryPath = `${baseUrl}/projects/${projectId}/blog_posts`;
  const blogPosts: Array<ProjectBlogPost> = [];
  let page = 1;
  const perPage = 100;

  while (true) {
    const res = await getData(
      token,
      queryPath,
      { page, per_page: perPage },
      config
    );
    const pagePosts = toArray<ProjectBlogPost>(
      (await deserialize(res.data)) as ProjectBlogPost | Array<ProjectBlogPost>
    );

    blogPosts.push(...pagePosts);

    const totalCount =
      (res.meta as { totalCount?: number; total_count?: number } | undefined)
        ?.totalCount ??
      (res.meta as { total_count?: number } | undefined)?.total_count ??
      pagePosts.length;

    if (blogPosts.length >= totalCount || pagePosts.length < perPage) {
      break;
    }

    page += 1;
  }

  return blogPosts;
};
